import React, { useState } from 'react'

import { Action, Context } from 'types/GlobalUserEvents'
import { DashboardStatus } from 'types/GlobalDashboardGroup'
import { TResourceVariant } from 'types/GlobalResource'
import { EPublicationStatus } from 'types/Enums'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'
import { useCustomer } from 'redux/hooks/Customer'
import { useDashboardGroup } from 'redux/hooks/DashboardGroups'
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'
import { useHistory } from 'react-router-dom'
import { useReport } from 'redux/hooks/Reports'
import { useReportActions } from 'redux/actions/Reports'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'

import OptionsMenu from 'components_new/molecules/OptionsMenu'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import AboutDashboardDialog from 'components_new/organisms/dialogs/AboutDashboardDialog'

import DeleteDialog from '../dialogs/DeleteDialog'
import EditTitleDialog from '../dialogs/EditTitleDialog'
import MoveDialog from '../dialogs/MoveDialog'

import { addRef } from 'utils/queryHandler'
import { getDashboardLimitReached } from 'utils/functions'

interface EditResourceMenuProps {
  deleteCallback?: () => void
  id: string
  onClose: () => void
  menuAnchorEl: HTMLAnchorElement | null
  variant: TResourceVariant
}

const EditResourceMenu = (props: EditResourceMenuProps) => {
  const {
    id,
    deleteCallback = () => {},
    menuAnchorEl,
    onClose,
    variant
  } = props

  const allowEdit = useAllowEditDashboardGroup(id)
  const history = useHistory()

  const dashboardGroup = useDashboardGroup(id)
  const report = useReport(id)

  // dashboards
  const {
    tryCopyDashboardGroup,
    tryDeleteDashboardGroup,
    tryUpdateDashboardGroup
  } = useDashboardGroupActions()

  // reports
  const { tryCopyReport, tryDeleteReport, tryUpdateReport } = useReportActions()

  const [dialog, setDialog] = useState<{
    data?: {
      folder_id?: string | null
      title?: string
    }
    open: boolean
    variant: 'delete' | 'edit' | 'move'
  }>({ open: false, variant: 'delete' })

  const [publishOpen, setPublishOpen] = useState(false)

  const customer = useCustomer()
  const { isHomepal } = useAccountSettings()
  const { t } = useTranslation()

  // checks
  if (variant === 'dashboard' && !dashboardGroup) return null
  if (variant === 'report' && !report) return null

  // handlers
  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      open: false
    }))
  }

  const handleDelete = (callback: () => void) => {
    if (dashboardGroup) {
      tryDeleteDashboardGroup(dashboardGroup.id, dashboardGroup.status, () => {
        callback()
        deleteCallback()
      })
    }

    if (report) {
      tryDeleteReport(report.id, () => {
        callback()
        deleteCallback()
      })
    }

    handleCloseDialog()
  }

  const handleDuplicate = () => {
    onClose()

    if (dashboardGroup) {
      tryCopyDashboardGroup(dashboardGroup.id, (id) =>
        history.push(addRef(`/dashboards/${id}`, Context.DUPLICATE))
      )
    }

    if (report) {
      tryCopyReport(report.id, (id) => history.push(`/reports/${id}`))
    }
  }

  const handleEdit = (value: string) => {
    if (dashboardGroup) {
      tryUpdateDashboardGroup(dashboardGroup.id, { title: value })
    }

    if (report) {
      tryUpdateReport(report.id, { title: value })
    }

    handleCloseDialog()
  }

  const handleMove = (folderId: string | null) => {
    if (dashboardGroup) {
      tryUpdateDashboardGroup(dashboardGroup.id, {
        folder_id: folderId
      })
    }

    if (report) {
      tryUpdateReport(report.id, { folder_id: folderId })
    }

    handleCloseDialog()
  }

  const handlePublish = () => {
    onClose()

    if (dashboardGroup) {
      if (isPublished) {
        tryUpdateDashboardGroup(
          dashboardGroup.id,
          { status: DashboardStatus.DRAFT },
          (e) => {
            if (!e) {
              UserEvents.log({
                action: Action.DASHBOARD_UNPUBLISHED,
                context: null,
                dashboard_id: null,
                dashboard_group_id: dashboardGroup.id
              })
            }
          }
        )
      } else {
        setPublishOpen(true)
      }
    }

    if (report) {
      if (isPublished) {
        tryUpdateReport(
          report.id,
          { status: EPublicationStatus.DRAFT },
          (e) => {
            if (!e) {
              UserEvents.log({
                action: Action.REPORT_UNPUBLISHED,
                context: null,
                report_id: report.id
              })
            }
          }
        )
      } else {
        setPublishOpen(true)
      }
    }
  }

  // open dialogs
  const handleOpenDialog = (value: 'delete' | 'edit' | 'move') => {
    setDialog({
      data: {
        folder_id:
          variant === 'dashboard'
            ? dashboardGroup?.folder_id
            : report?.folder_id,
        title: variant === 'dashboard' ? dashboardGroup?.title : report?.title
      },
      open: true,
      variant: value
    })
  }

  const isPublished =
    variant === 'dashboard'
      ? dashboardGroup?.status === DashboardStatus.PUBLISHED
      : report?.status === EPublicationStatus.PUBLISHED
  const limitReached =
    getDashboardLimitReached(isHomepal, customer) && variant === 'dashboard'

  return (
    <>
      <OptionsMenu
        anchorEl={menuAnchorEl}
        disableScrollLock={true}
        onClose={onClose}
        open={Boolean(menuAnchorEl)}
        options={[
          {
            disabled: !allowEdit,
            iconName: 'Edit',
            onClick: () => handleOpenDialog('edit'),
            title: t('global', 'change_name')
          },
          {
            disabled: !allowEdit,
            iconName: 'DriveFileMoveOutlined',
            onClick: () => handleOpenDialog('move'),
            title: t('global', 'move')
          },
          {
            disabled: limitReached,
            divider: true,
            iconName: 'Duplicate',
            onClick: handleDuplicate,
            title: t('global', 'duplicate'),
            tooltip: limitReached
              ? t('dashboards', 'dashboard_limit_reached_tooltip')
              : undefined
          },
          {
            disabled: !allowEdit || (!isPublished && limitReached),
            iconName: isPublished ? 'Unpublished' : 'Published',
            onClick: handlePublish,
            title: t('global', isPublished ? 'unpublish' : 'publish'),
            tooltip:
              !isPublished && limitReached
                ? t('dashboards', 'dashboard_limit_reached_tooltip')
                : undefined
          },
          {
            disabled: !allowEdit,
            iconName: 'Delete',
            onClick: () => handleOpenDialog('delete'),
            title: t('global', 'delete')
          }
        ]}
      />

      {/* publish dashboard */}
      {dashboardGroup ? (
        <AboutDashboardDialog
          dashboardGroup={dashboardGroup}
          onClose={() => setPublishOpen(false)}
          open={publishOpen}
          variant="publish"
          setVariant={() => {}}
        />
      ) : null}

      {/* publish report */}
      {report ? (
        <SimpleDialog
          actions={
            <>
              <Button variant="text" onClick={() => setPublishOpen(false)}>
                {t('global', 'cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  tryUpdateReport(
                    report.id,
                    { status: EPublicationStatus.PUBLISHED },
                    (e) => {
                      if (!e) {
                        UserEvents.log({
                          action: Action.REPORT_PUBLISHED,
                          context: null,
                          report_id: report.id
                        })
                      }

                      setPublishOpen(false)
                    }
                  )
                }}
              >
                {t('global', 'publish')}
              </Button>
            </>
          }
          onClose={close}
          open={publishOpen}
          title={t('global', 'publish')}
        >
          <Text color="text.primary">
            {/* $TODO: @axel*/}
            {t('global', 'publish')} {report.title.toLowerCase()}
          </Text>
        </SimpleDialog>
      ) : null}

      <DeleteDialog
        onClose={handleCloseDialog}
        onDelete={handleDelete}
        open={dialog.open && dialog.variant === 'delete'}
        type={t('global', variant, true)}
        title={dialog.data?.title}
      />

      <EditTitleDialog
        onClose={handleCloseDialog}
        onSubmit={handleEdit}
        open={dialog.open && dialog.variant === 'edit'}
        title={dialog.data?.title}
      />

      <MoveDialog
        folderId={dialog.data?.folder_id ?? null}
        onClose={handleCloseDialog}
        onSubmit={handleMove}
        open={dialog.open && dialog.variant === 'move'}
        title={dialog.data?.title}
      />
    </>
  )
}

export default EditResourceMenu
